import { ChangeDetectionStrategy, Component, computed, OnInit, signal, inject } from '@angular/core';
import {ElectronicDocumentsService} from '../../../services/electronic-documents.service';
import {MemberStateService} from '../../../services/member-state.service';
import {FormatService} from '../../../services/format.service';
import {UserContextService} from '../../../services/user-context.service';
import {AnalyticsService} from '../../../services/analytics.service';
import {concatMap, map, startWith, switchMap, take, tap} from 'rxjs/operators';
import {ElectronicDocumentReference} from '../../../model/electronic-document-reference';
import {AsyncPipe, DatePipe} from '@angular/common';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {Router} from '@angular/router';
import {format, startOfDay, startOfYear, subYears} from 'date-fns';
import {Member} from '../../../model/member';
import {StorageService} from '../../../services/storage.service';
import {RoutingStateService} from '../../../services/routing-state.service';
import {DatePickerOptions} from '../../../model/date-picker-options';
import {Observable, Subject} from 'rxjs';
import { IntroductionComponent } from '../../shared/introduction/introduction.component';
import { FilterComponent } from '../../shared/filter/filter.component';
import { DateFilterComponent } from '../../shared/filter/date-filter/date-filter.component';
import { DropdownFilterComponent } from '../../shared/filter/dropdown-filter/dropdown-filter.component';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { AlertComponent } from '../../shared/alert/alert.component';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { CapitalCasePipe } from '../../../pipes/capital-case.pipe';
import {ViewState, toViewState} from '../../../model/view-state';

interface EDocsOverviewData {
  documents: ElectronicDocumentReference[];
  listOfYear: string[];
}

@Component({
    selector: 'myabvv-e-docs-overview',
    standalone: true,
    templateUrl: './e-docs-overview.component.html',
    styleUrls: ['./e-docs-overview.component.css'],
    imports: [
      IntroductionComponent, FilterComponent, DateFilterComponent, DropdownFilterComponent, LoaderComponent,
      AlertComponent, PaginationComponent, AsyncPipe, DatePipe, TranslatePipe, CapitalCasePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EDocsOverviewComponent implements OnInit {
  private electronicDocumentsService = inject(ElectronicDocumentsService);
  private translateService = inject(TranslateService);
  private datePipe = inject(DatePipe);
  private memberStateService = inject(MemberStateService);
  private formatService = inject(FormatService);
  private routingState = inject(RoutingStateService);
  private router = inject(Router);
  private storage = inject(StorageService);
  private userContext = inject(UserContextService);
  private analyticsService = inject(AnalyticsService);


  state$: Observable<ViewState<EDocsOverviewData>>;
  private fetchTrigger$ = new Subject<void>();

  startMonth: Date;
  endMonth: Date;
  datePickerOptionsStartDate: DatePickerOptions;
  datePickerOptionsEndDate: DatePickerOptions;
  types: { id: string; label: string; show?: boolean }[];
  activeYear = signal('');
  electronicDocumentsOverview = signal<ElectronicDocumentReference[]>([]);
  listOfYear = computed(() => {
    const years = new Set<string>();
    this.electronicDocumentsOverview().forEach(edr => {
      const year = this.datePipe.transform(edr.referenceMonth, 'yyyy');
      if (year) years.add(year);
    });
    return [...years].sort((a, b) => Number(b) - Number(a));
  });
  partialElectronicDocumentsOverview = computed(() =>
    this.electronicDocumentsOverview()
      .filter(edr => edr.referenceMonth.toString().substring(0, 4) === this.activeYear())
  );
  sortedDocuments = computed(() =>
    [...this.partialElectronicDocumentsOverview()].sort((a, b) =>
      b.referenceMonth.localeCompare(a.referenceMonth))
  );
  private member: Member;
  calendarMaxEndMonth = new Date();
  selectedType: string;

  ngOnInit() {
    this.initializeTypes();
    this.initializeDates();
    this.initializeDatePickerOptionsStartDate();
    this.initializeDatePickerOptionsEndDate();
    this.initFetchPipeline();
  }

  onApplyFilter() {
    this.removeSelectedYearInStore();
    this.fetchTrigger$.next();
  }

  onReinitializeFilters() {
    this.removeSelectedYearInStore();
    this.initializeDates(true);
    this.selectedType = this.types[0].id;
    this.types = this.types.map(type => ({...type, show: type.id === this.selectedType}));
    this.fetchTrigger$.next();
  }

  onGoToDetail(ticketNumberXL: string) {
    const startMonthStr = format(this.startMonth, 'yyyy-MM');
    const endMonthStr = format(this.endMonth, 'yyyy-MM');
    const selectedType = this.selectedType;
    const memberInss = this.member.personalInformation.inss;

    this.analyticsService.trackEvent('view_edoc_detail', {
      documentType: this.selectedType
    });

    this.storeSelectedYear();
    this.router.navigate(['/unemployment/e-docs/detail'],
      {queryParams: {ticketNumberXL, startMonthStr, endMonthStr, selectedType, memberInss }}).then().catch();
  }

  onChangeStartDate(newDate: Date) {
    this.startMonth = newDate;

    if (this.startMonth > this.endMonth) {
      this.endMonth = this.startMonth;
    }
  }

  onChangeEndDate(newDate: Date) {
    this.endMonth = newDate;

    if (this.endMonth < this.startMonth) {
      this.startMonth = this.endMonth;
    }
    this.activeYear.set(this.endMonth.getFullYear().toString());
  }

  onChangeType(type: string) {
    this.selectedType = type;
  }

  private initFetchPipeline() {
    this.state$ = this.fetchTrigger$.pipe(
      startWith(void 0),
      switchMap(() => {
        this.storeDates();
        this.storeDocumentType();
        const isInternalUser = this.userContext.isInternalUser();

        const startMonthStr = format(this.startMonth, 'yyyy-MM');
        const endMonthStr = format(this.endMonth, 'yyyy-MM');

        // Use /me endpoint for external users, /{inss} for internal users
        const eDocs$ = isInternalUser
          ? this.memberStateService.getMember().pipe(
              take(1),
              concatMap(member => {
                this.member = member;
                return this.electronicDocumentsService
                  .getElectronicDocumentsOverview(member.personalInformation.inss, this.selectedType, startMonthStr, endMonthStr);
              })
            )
          : this.memberStateService.getMember().pipe(
              take(1),
              concatMap(member => {
                this.member = member;
                return this.electronicDocumentsService
                  .getMyElectronicDocumentsOverview(this.selectedType, startMonthStr, endMonthStr);
              })
            );

        return eDocs$.pipe(
          tap(res => {
            this.electronicDocumentsOverview.set(res);
            if (res.length > 0) {
              const storedYear = this.storage.getSelectedYearElectronicDocuments();
              const years = this.listOfYear();
              this.activeYear.set(storedYear && years.includes(storedYear) ? storedYear : years[0] || '');
            }
          }),
          map(documents => ({
            documents,
            listOfYear: this.listOfYear()
          })),
          toViewState()
        );
      })
    );
  }

  getPartialElectronicDocumentsOverview(activeYear: string) {
    this.activeYear.set(activeYear);
  }

  initializeTypes() {
    const storedSelectedType = this.storage.getSelectedElectronicDocumentType();
    this.types = this.formatService.getElectronicDocumentTypes();

    if (storedSelectedType && this.routingState.getPreviousUrl().includes('detail')) {
      this.selectedType = storedSelectedType;
    } else {
      this.selectedType = this.types[0].id;
    }

    this.types = this.types.map(type => ({...type, show: type.id === this.selectedType}));
  }

  initializeDates(useDefault: boolean = false) {
    const startDateLocalStorage = new Date(this.storage.getStartDate());
    const endDateLocalStorage = new Date(this.storage.getEndDate());

    if (!useDefault && endDateLocalStorage && this.routingState.getPreviousUrl().includes('detail')) {
      this.endMonth = startOfDay(endDateLocalStorage);
    } else {
      this.endMonth = startOfDay(new Date());
    }

    if (!useDefault && startDateLocalStorage && this.routingState.getPreviousUrl().includes('detail')) {
      this.startMonth = new Date(startDateLocalStorage);
    } else {
      this.startMonth = subYears(startOfYear(new Date()), 1);
    }

    if (!this.activeYear()) {
      this.activeYear.set(this.endMonth.getFullYear().toString());
    }

    this.storeDates();
  }

  initializeDatePickerOptionsStartDate() {
    this.datePickerOptionsStartDate = {
      format: 'mm-yy',
      endDate: this.calendarMaxEndMonth, // the MAX date that can be selected
      startView: 'month'
    };
  }

  initializeDatePickerOptionsEndDate() {
    this.datePickerOptionsEndDate = {
      format: 'mm-yy',
      endDate: this.calendarMaxEndMonth, // the MAX date that can be selected
      startView: 'month'
    };
  }

  storeDates() {
    this.storage.setStartDate(this.startMonth.toString());
    this.storage.setEndDate(this.endMonth.toString());
  }

  storeDocumentType() {
    this.storage.setSelectedElectronicDocumentType(this.selectedType);
  }

  storeSelectedYear() {
    this.storage.setSelectedYearElectronicDocuments(this.activeYear());
  }

  removeSelectedYearInStore() {
    this.storage.removeSelectedYearElectronicDocuments();
  }

  hasDocuments(data: EDocsOverviewData | null): boolean {
    return (data?.documents?.length ?? 0) > 0;
  }

}
